import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';

export interface CsvConfigRow {
  id: string;
  startAfter: number;
  stopAfter: number;
  fileReady?: boolean;
}

interface CsvConfigUploadProps {
  onConfigLoaded: (config: CsvConfigRow[]) => void;
  config: CsvConfigRow[];
}

export const CsvConfigUpload: React.FC<CsvConfigUploadProps> = ({
  onConfigLoaded,
  config
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setIsUploading(true);

    try {
      // Step 1: Pre-parse CSV preview for debugging
      const previewReader = new FileReader();
      previewReader.onload = (e) => {
        const text = e.target?.result as string;
        console.log('📄 Raw CSV (first 500 chars):', text.substring(0, 500));
        console.log('📄 Raw CSV (first 3 lines):', text.split('\n').slice(0, 3));
      };
      previewReader.readAsText(file);

      // Step 2: Main parse with BOM handling
      const mainReader = new FileReader();
      mainReader.onload = (e) => {
        let text = e.target?.result as string;
        
        // Remove BOM if present
        if (text.charCodeAt(0) === 0xFEFF) {
          console.log('🔧 Removed BOM from CSV');
          text = text.substring(1);
        }
        
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            try {
              console.log('📊 Papa Parse Results:', {
                fields: results.meta.fields,
                totalRows: results.data.length,
                firstRow: results.data[0]
              });

              const parsedConfig: CsvConfigRow[] = [];
              
              for (let i = 0; i < results.data.length; i++) {
                const row = results.data[i] as any;
                
                // Log EVERY row including empty ones
                console.log(`\n📋 Row ${i + 1}:`, row);
                
                // Trim all values manually
                let id = (row['ID'] || row['id'] || row['File'] || row['file'] || '').toString().trim();
                const rawStart = (row['START AFTER'] || row['start_after'] || row['Start After'] || '').toString().trim();
                const rawStop = (row['STOP AFTER'] || row['stop_after'] || row['Stop After'] || '').toString().trim();
                const fileReady = (row['FILE READY?'] || row['file_ready'] || row['File Ready'] || true).toString().trim();
                
                console.log(`  ID: "${id}"`);
                console.log(`  Raw START: "${rawStart}" (type: ${typeof rawStart})`);
                console.log(`  Raw STOP: "${rawStop}" (type: ${typeof rawStop})`);
                
                const startAfter = parseInt(rawStart || '0');
                const stopAfter = parseInt(rawStop || '0');
                
                console.log(`  Parsed START: ${startAfter}`);
                console.log(`  Parsed STOP: ${stopAfter}`);
                
                // Sanitize ID to remove non-printable characters and normalize spaces
                if (id) {
                  const originalId = id;
                  id = id
                    .replace(/[\r\n\t]/g, '')           // Remove CR/LF/Tab
                    .replace(/[^\x20-\x7E]/g, '')       // Remove non-printable
                    .replace(/\s*-\s*/g, '-')           // Normalize hyphens
                    .replace(/\s+/g, ' ')               // Normalize spaces
                    .replace(/^["\s]+|["\s]+$/g, '')    // Remove quotes
                    .trim();
                  
                  if (originalId !== id) {
                    console.log(`  🔧 ID sanitized: "${originalId}" → "${id}"`);
                  }
                }
                
                if (!id) {
                  console.log('  ⏭️ SKIPPED: Empty ID');
                  continue;
                }

                if (isNaN(startAfter) || isNaN(stopAfter)) {
                  console.error(`  ❌ INVALID NUMBERS: START=${rawStart}, STOP=${rawStop}`);
                  toast.error(`Row ${i + 1}: Invalid numbers for ${id}`);
                  continue;
                }

                if (stopAfter <= startAfter) {
                  console.error(`  ❌ VALIDATION FAILED: START=${startAfter} >= STOP=${stopAfter}`);
                  toast.error(`Row ${i + 1}: ${id} - STOP (${stopAfter}) must be > START (${startAfter})`);
                  continue;
                }
                
                console.log(`  ✅ VALID`);
                parsedConfig.push({
                  id,
                  startAfter,
                  stopAfter,
                  fileReady: fileReady === 'true' || fileReady === 'TRUE' || fileReady === '1' || fileReady === 'Y' || fileReady === 'y'
                });
              }

              const summary = {
                total: results.data.length,
                valid: parsedConfig.length,
                skipped: results.data.length - parsedConfig.length
              };

              console.log('\n📊 PARSE SUMMARY:', summary);

              if (parsedConfig.length === 0) {
                console.error('❌ No valid rows found!');
                console.error('Available fields:', results.meta.fields);
                toast.error(`No valid rows. Check console. Expected columns: ID, START AFTER, STOP AFTER`);
                setIsUploading(false);
                return;
              }

              if (summary.skipped > 0) {
                toast.warning(`Loaded ${summary.valid}/${summary.total} rows. ${summary.skipped} skipped. Check console for details.`);
              }

            // Check for symbol diversity to warn about potential issues
            const uniqueSymbols = new Set();
            parsedConfig.forEach(config => {
              const symbolMatch = config.id.match(/^([A-Z]{3,8})/i);
              if (symbolMatch) {
                uniqueSymbols.add(symbolMatch[1].toUpperCase());
              }
            });

            onConfigLoaded(parsedConfig);
            
            const symbolCount = uniqueSymbols.size;
            if (symbolCount <= 2) {
              toast.warning(`Loaded ${parsedConfig.length} config entries but only ${symbolCount} unique symbols detected. Check if this is correct.`);
            } else {
              toast.success(`Loaded ${parsedConfig.length} configuration entries covering ${symbolCount} symbols`);
            }
            
          } catch (error) {
            console.error('Error parsing CSV config:', error);
            toast.error('Failed to parse CSV configuration file');
          } finally {
            setIsUploading(false);
          }
        },
        error: (error) => {
          console.error('Papa parse error:', error);
          toast.error('Failed to read CSV file');
          setIsUploading(false);
        }
      });
      };
      mainReader.readAsText(file, 'UTF-8');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
      setIsUploading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          CSV Position Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="csv-config-upload">Upload Configuration CSV</Label>
          <div className="flex items-center gap-2">
            <Input
              id="csv-config-upload"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={isUploading}
              onClick={() => document.getElementById('csv-config-upload')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? 'Uploading...' : 'Browse'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Expected format: ID | START AFTER | STOP AFTER | FILE READY?
          </p>
        </div>

        {config.length > 0 && (
          <div className="space-y-2">
            <Label>Configuration Preview ({config.length} entries)</Label>
            <div className="max-h-40 overflow-y-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 border-b">ID</th>
                    <th className="text-left p-2 border-b">Start After</th>
                    <th className="text-left p-2 border-b">Stop After</th>
                    <th className="text-left p-2 border-b">Range</th>
                    <th className="text-left p-2 border-b">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {config.map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2 font-mono text-xs">{row.id}</td>
                      <td className="p-2">{row.startAfter}</td>
                      <td className="p-2">{row.stopAfter}</td>
                      <td className="p-2 text-muted-foreground">
                        Loss {row.startAfter + 1} - {row.stopAfter}
                      </td>
                      <td className="p-2">
                        {row.fileReady !== false ? (
                          <CheckCircle className="h-3 w-3 text-primary" />
                        ) : (
                          <XCircle className="h-3 w-3 text-destructive" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {config.length > 0 && (
          <div className="space-y-3">
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-primary">
                    Smart Matching System:
                  </p>
                  <p className="text-xs text-primary/80">
                    • IDs like "APTUSDT.P-1.3-1M" will match files containing "APTUSDT.P-1.3-1M"<br/>
                    • START AFTER = 2, STOP AFTER = 4 selects positions for losses 3 and 4<br/>
                    • Position sizes are mapped as: 1st size = loss 1, 2nd size = loss 2, etc.<br/>
                    • Symbol-first matching ensures better accuracy
                  </p>
                </div>
              </div>
            </div>
            
            {/* Show available symbols */}
            <div className="p-2 bg-muted/30 border border-border/40 rounded-md">
              <p className="text-xs font-medium text-foreground mb-1">Available Config IDs:</p>
              <div className="text-xs text-muted-foreground font-mono">
                {config.slice(0, 5).map(c => c.id).join(', ')}
                {config.length > 5 && ` ...and ${config.length - 5} more`}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};